/**
 * @fileoverview 관리자 목록 조회를 위한 커스텀 훅
 * @description 여러 컴포넌트에서 반복되는 loadAdminList 패턴을 통합
 *
 * 사용처: SchedulerManager, PromptManager, PredictionPanel, PredictionManager,
 *         ServerConfig, AdminNotionTab, AdminPsynetTab 등
 */

import { useCallback } from 'react';
import { getAdminList } from '../api';
import { useDataFetch } from './useDataFetch';
import type { UseDataFetchOptions } from './useDataFetch';

/**
 * 관리자 정보 타입
 * @description 컴포넌트들에서 공통으로 사용하는 관리자 목록 아이템 타입
 */
export interface AdminInfo {
  admin_id: number;
  username: string;
  display_name: string;
  role: string;
  is_active?: boolean;
  last_login?: string | null;
  created_at?: string | null;
}

/**
 * useAdminList 훅 옵션
 * @interface UseAdminListOptions
 */
interface UseAdminListOptions {
  /** 마운트 시 자동 호출 여부 (기본값: true) */
  immediate?: boolean;
  /** 성공 시 콜백 함수 */
  onSuccess?: (admins: AdminInfo[]) => void;
  /** 실패 시 콜백 함수 */
  onError?: (error: Error) => void;
}

/**
 * useAdminList 훅 반환 타입
 * @interface UseAdminListReturn
 */
interface UseAdminListReturn {
  /** 관리자 목록 */
  adminList: AdminInfo[];
  /** 로딩 상태 */
  loading: boolean;
  /** 에러 객체 */
  error: Error | null;
  /** 관리자 목록 재조회 함수 */
  refetch: () => Promise<void>;
}

/**
 * 관리자 목록을 조회하는 커스텀 훅
 *
 * @description
 * - 여러 컴포넌트에서 동일하게 반복되던 loadAdminList / loadAdminListForSync 패턴을 통합
 * - getAdminList API를 호출하고, 응답에서 admins 배열을 추출하여 AdminInfo[]로 정규화
 * - useDataFetch를 내부적으로 활용
 *
 * @example
 * ```tsx
 * // 기본 사용 - 마운트 시 자동 조회
 * const { adminList, loading, refetch } = useAdminList();
 *
 * // administrator 역할일 때만 조회
 * const { adminList, loading, refetch } = useAdminList({
 *   immediate: isAdministrator
 * });
 *
 * // 조회 후 첫 번째 관리자 자동 선택
 * const { adminList } = useAdminList({
 *   onSuccess: (admins) => {
 *     if (admins.length > 0) {
 *       setSelectedAdminId(admins[0].admin_id);
 *     }
 *   }
 * });
 * ```
 *
 * @param {UseAdminListOptions} options - 훅 옵션
 * @returns {UseAdminListReturn} 관리자 목록 상태와 재조회 함수
 */
export function useAdminList(options: UseAdminListOptions = {}): UseAdminListReturn {
  const { immediate = true, onSuccess, onError } = options;

  const fetchAdmins = useCallback(async (): Promise<AdminInfo[]> => {
    const response = await getAdminList();
    const adminsData = Array.isArray(response) ? response : response.admins || [];
    return adminsData.map((a: any) => ({
      admin_id: a.admin_id,
      username: a.username,
      display_name: a.display_name,
      role: a.role,
      is_active: a.is_active,
      last_login: a.last_login,
      created_at: a.created_at
    }));
  }, []);

  const fetchOptions: UseDataFetchOptions<AdminInfo[]> = {
    immediate,
    initialData: [],
    onSuccess,
    onError
  };

  const { data, loading, error, refetch } = useDataFetch<AdminInfo[]>(
    fetchAdmins,
    fetchOptions
  );

  return {
    adminList: data ?? [],
    loading,
    error,
    refetch
  };
}

export type { UseAdminListOptions, UseAdminListReturn };
export default useAdminList;
